import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Hash, Link2, Loader2, Lock, X } from 'lucide-react';
import { apiService } from '../services/api';

// Projet sans channel Slack : Nexus propose de le créer et d'y inviter l'équipe du projet. Une fois lié, Nexus suit
// le channel : chaque message est trié sur le Mac d'Innowide et ce qui compte rejoint le journal du projet.
const SlackChannelDialog = ({ projectId, open, onClose, onCreated, onLinkExisting }) => {
  const [info, setInfo] = useState(null);
  const [name, setName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setInfo(null);
    setResult(null);
    setError(null);
    apiService.get(`/projects/${projectId}/slack`).then((data) => {
      setInfo(data);
      setName(data.suggested_name || '');
      setSelected((data.team || []).map((p) => p.id));
    }).catch((err) => setError(err.message));
  }, [open, projectId]);

  if (!open) return null;

  const create = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await apiService.post(`/projects/${projectId}/slack/channel`, {
        name: name.trim(), is_private: isPrivate, employee_ids: selected });
      setResult(created);
      onCreated?.(created.link);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Portail : la fenêtre ne doit ni hériter des transformations d'une carte, ni lui transmettre ses clics.
  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-slate-900/40 p-0 sm:items-center sm:p-4"
      onClick={(event) => { event.stopPropagation(); onClose(); }}>
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-5 text-slate-800 shadow-2xl sm:rounded-2xl"
        onClick={(event) => event.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Channel Slack du projet</h2>
            <p className="text-xs text-slate-500">
              Nexus suivra le channel : chaque message est trié sur le Mac d'Innowide, et ce qui compte rejoint le journal du projet.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-slate-100" aria-label="Fermer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="space-y-3 text-sm">
            <p className="flex items-center gap-2 font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" /> #{result.name} créé et lié au projet</p>
            {result.invited?.length > 0 && <p className="text-slate-600">Invités : {result.invited.map((p) => p.name).join(', ')}.</p>}
            {result.not_invited?.length > 0 && (
              <p className="text-amber-700">Pas invités (compte Slack introuvable) : {result.not_invited.map((p) => p.name).join(', ')}.</p>
            )}
            <div className="flex gap-2">
              <a href={result.link} target="_blank" rel="noreferrer"
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">Ouvrir dans Slack</a>
              <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">Fermer</button>
            </div>
          </div>
        ) : !info ? (
          error ? <p className="text-sm text-rose-600">{error}</p>
            : <p className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Chargement…</p>
        ) : !info.enabled ? (
          <p className="text-sm text-slate-500">Slack n'est pas configuré sur ce serveur.</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">Nom du channel</label>
              <div className="flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-2 focus-within:border-blue-300">
                {isPrivate ? <Lock className="h-4 w-4 text-slate-400" /> : <Hash className="h-4 w-4 text-slate-400" />}
                <input value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, '-'))} maxLength={80}
                  className="min-w-0 flex-1 text-sm outline-none" />
              </div>
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} /> Channel privé
              </label>
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold text-slate-600">Équipe du projet à inviter</p>
              {(info.team || []).length === 0 ? (
                <p className="text-xs text-slate-400">Aucun membre identifié (responsable, rôles, heures récentes) : tu seras seul invité.</p>
              ) : (
                <ul className="max-h-48 space-y-1 overflow-y-auto">
                  {info.team.map((person) => (
                    <li key={person.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm hover:bg-slate-50">
                        <input type="checkbox" checked={selected.includes(person.id)} onChange={() => toggle(person.id)} />
                        {person.name}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-1 text-[11px] text-slate-400">Tu es ajouté d'office.</p>
            </div>
            {error && <p className="text-xs text-rose-600">{error}</p>}
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={create} disabled={saving || !name.trim()}
                className="flex items-center gap-1.5 rounded-xl bg-[#4A154B] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hash className="h-4 w-4" />} Créer le channel
              </button>
              {onLinkExisting && (
                <button type="button" onClick={() => { onClose(); onLinkExisting(); }}
                  className="flex items-center gap-1 rounded-xl px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100">
                  <Link2 className="h-3.5 w-3.5" /> Lier un channel existant
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default SlackChannelDialog;
